import {Box, Typography} from "@mui/material";
import GeneralStepper from "../../components/GeneralStepper";
import CategoryData from "../category/CategoryData";
import {useRef, useState} from "react";
import useCategoryQueries from "../../hooks/queries/useCategoryQueries";
import {useSnackbar} from "notistack";
import AuthBase from "../AuthBase";
import ServiceData from "../category/service/components/ServiceData";
import useServiceQueries from "../../hooks/queries/useServiceQueries";
import PriceData from "../category/service/components/PriceData";
import usePriceQueries from "../../hooks/queries/usePriceQueries";
import WorkHours from "../colleague/components/WorkHours";
import useBusinessQueries from "../../hooks/queries/useBusinessQueries";
import Loading from "../../components/Loading";

const Wizard = () => {
    const [created, setCreated] = useState({})
    const categoryForm = useRef(null)
    const serviceForm = useRef(null)
    const priceForm = useRef(null)
    const {createCategoryMutation, updateCategoryMutation} = useCategoryQueries()
    const {createServiceMutation, updateServiceMutation} = useServiceQueries()
    const {createPriceMutation, updatePriceMutation, usePriceServiceQuery} = usePriceQueries()
    const {businessGetBusinessQuery} = useBusinessQueries()
    const {data: business, isPending: businessLoading} = businessGetBusinessQuery

    const {enqueueSnackbar} = useSnackbar()
    const {data: prices, isPending: pricesLoading} = usePriceServiceQuery(created?.service?.id)

    const handleError = errors => {
        errors.errors.forEach(err => {
            if (err.field) {
                enqueueSnackbar(`${err.message} - ${err.field}`, {variant: 'error'});
            } else {
                enqueueSnackbar(err.message, {variant: 'error'});
            }
        });
    }

    const newCategory = data => createCategoryMutation.mutate(data, {
        onSuccess: response => setCreated(prev => ({...prev, category: response})),
        onError: errors => handleError(errors)
    })

    const updateCategory = data => updateCategoryMutation.mutate({id: created.category.id, data}, {
        onSuccess: response => setCreated(prev => ({...prev, category: response})),
        onError: errors => handleError(errors)
    })

    const newService = data => createServiceMutation.mutate({
        ...data,
        is_public: true,
        category: created?.category?.id
    }, {
        onSuccess: response => setCreated(prev => ({...prev, service: response})),
        onError: errors => handleError(errors)
    })

    const updateService = data => updateServiceMutation.mutate({
        id: created.service.id,
        data: {...data, is_public: true, category: created?.category?.id}
    }, {
        onSuccess: response => setCreated(prev => ({...prev, service: response})),
        onError: errors => handleError(errors)
    })

    const newPrice = data => createPriceMutation.mutate({...data, service: created?.service?.id}, {
        onSuccess: response => setCreated(prev => ({...prev, price: response})),
        onError: errors => handleError(errors)
    })

    const updatePrice = data => updatePriceMutation.mutate({id: created.price.id, data: {...created.price, ...data}}, {
        onSuccess: response => setCreated(prev => ({...prev, price: response})),
        onError: errors => handleError(errors)
    })

    const steps = [
        {
            nextLabel: 'Kategória mentése',
            component: <Box className='white-card'>
                <Typography variant='headline6' color='primary.600' gutterBottom>Kategória hozzáadása</Typography>
                <Typography variant='body2' color='#6F8E9A' gutterBottom textAlign='justify'>A szolgáltatások
                    áttekinthetősége érdekében minden elem kategóriákba van rendezve 📂
                    Első lépésként hozz létre egy kategóriát, majd ezt követően tudod a megfelelő szolgáltatásokat
                    hozzáadni az adott kategóriához ➕✨

                    Ez a felépítés segít abban, hogy a szolgáltatásaid könnyen kezelhetők és gyorsan megtalálhatók
                    legyenek.</Typography>
                <CategoryData reference={categoryForm} obj={created?.category || null}/>
            </Box>,
            validate: () => {
                const data = categoryForm.current.getData()
                if (!data) return {
                    valid: false,
                    error: 'Javítsd a hibákat a továbblépéshez!'
                }
                if (typeof data?.picture === 'string') delete data['picture'] // Ugh... If the picture is already uploaded leave it alone
                if (Object.keys(data).length > 0) {
                    if (created?.category) updateCategory(data)
                    else newCategory(data)
                }
                return {
                    valid: true,
                    error: ''
                }
            }
        },
        {
            nextLabel: 'Szolgáltatás mentése',
            component: <Box className='white-card'>
                <Typography variant='headline6' color='primary.600'>Szolgáltatás hozzáadása
                    ehhez: {created?.category?.name}</Typography>
                <Typography variant='body2' color='#6F8E9A' gutterBottom textAlign='justify'>A szolgáltatás
                    hozzáadásakor add meg az alapvető adatokat, amelyek a működéshez szükségesek 🛠️
                    A részletes beállítások és szerkesztési lehetőségek később a Kategóriák menüpont alatt lesznek
                    elérhetők 📂⚙️

                    Ott további opciókat találsz a szolgáltatás finomhangolásához, így bármikor pontosíthatod és testre
                    szabhatod a beállításokat ✨ Az itt hozzáadott szolgáltatást automatikusan láthatóra állítjuk, hogy a
                    befejezéskor meg tudd nézni az új oldaladat.</Typography>
                <ServiceData reference={serviceForm} obj={created?.service || null}/>
            </Box>,
            validate: () => {
                const data = serviceForm.current.getData()
                if (!data) return {
                    valid: false,
                    error: 'Javítsd a hibákat a továbblépéshez!'
                }
                if (typeof data?.image === 'string') delete data['image'] // Ugh... If the picture is already uploaded leave it alone
                if (Object.keys(data).length > 0) {
                    if (created.service) updateService(data)
                    else newService(data)
                }
                return {
                    valid: true,
                    error: ''
                }
            }
        },
        {
            nextLabel: 'Ár mentése',
            component: <Box className='white-card'>
                <Typography variant='headline6' color='primary.600'>Ár beállítása
                    ehhez: {created?.service?.name}</Typography>
                <Typography variant='body2' color='#6F8E9A' gutterBottom textAlign='justify'>A következő lépésben meg
                    kell adnod, hogy melyik kolléga végzi el az adott szolgáltatást, valamint azt is, hogy milyen áron
                    💼💰
                    Itt tudod összerendelni a szolgáltatást a megfelelő munkatárssal, így a foglalások során mindig
                    egyértelmű lesz, ki és milyen feltételekkel nyújtja a szolgáltatást.

                    Ez a beállítás később is módosítható, ezért nyugodtan finomíthatod az árakat vagy a hozzárendelt
                    kollégákat, ha változás történik 🔧✨</Typography>
                <PriceData reference={priceForm} prices={prices} obj={created?.price || null}/>

            </Box>,
            validate: () => {
                const data = priceForm.current.getData()
                if (!data) return {
                    valid: false,
                    error: 'Javítsd a hibákat a továbblépéshez!'
                }
                if (Object.keys(data).length > 0) {
                    if (created.price) updatePrice(data)
                    else newPrice(data)
                }
                return {
                    valid: true,
                    error: ''
                }
            }
        },
        {
            nextLabel: 'Munkaidő mentése',
            component: <WorkHours userId={created?.price?.colleague?.id}/>,
            validate: () => ({
                valid: true,
                error: 'A továbblépéshez szükséges megadni és menteni a munkaidő adatokat!'
            })
        },
        {
            nextLabel: 'Megnézem a foglaló oldalt',
            component: <Box className='white-card'>
                <Typography variant='headline6' color='primary.600'>Weboldal beállítása</Typography>
                <Typography variant='body2' color='#6F8E9A' gutterBottom textAlign='justify'>A következő lépés a
                    weboldal testreszabása 🌐
                    Ezen a felületen most nem szükséges semmit beállítanod, mivel a lehetőségek száma igen nagy, és a
                    részletes konfiguráció külön menüpontban érhető el.

                    Ha később szeretnél kísérletezni a megjelenéssel vagy a beállításokkal, ezt bármikor megteheted a
                    Weboldal menüpont alatt 🎨⚙️
                    Így akkor foglalkozhatsz vele, amikor már pontosan tudod, mire van szükséged.</Typography>
            </Box>,
            validate: () => ({
                valid: true,
                error: ''
            })
        },
    ];

    return <AuthBase label='Varázsló'>
        <div className='main'>
            <GeneralStepper steps={steps}
                            onFinish={() => window.open(`https://${business?.subdomain}.dinobooking.hu/`)}/>
        </div>
        <Loading isLoading={
            createCategoryMutation.isPending ||
            updateCategoryMutation.isPending ||
            createServiceMutation.isPending ||
            updateServiceMutation.isPending ||
            createPriceMutation.isPending ||
            updatePriceMutation.isPending ||
            businessLoading
        }/>
    </AuthBase>
}

export default Wizard